import { ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, ViewChildren } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';
import { DOCUMENT } from '@angular/common';
import { AppLinkSelectorComponent } from './app-link-selector/app-link-selector.component';
import { FeatureToggleService } from 'src/app/services/feature-toggle/feature-toggle.service';
import { MatDialog } from '@angular/material/dialog';
import { OptOutDialogComponent } from './opt-out-dialog/opt-out-dialog.component';
import { AdminHandlerService } from 'src/app/services/admin-handler/admin-handler.service';
import { IntroAnimationService, IntroAnimationStatus } from 'src/app/services/intro-animation/intro-animation.service';
import { Subscription } from 'rxjs';
import { LaunchControlService } from 'src/app/services/launch-control/launch-control.service';
import { AppNavV2Service } from 'src/app/services/app-nav-v2/app-nav-v2.service';
import { NextLeaderLinkGeneratorV2Service } from 'src/app/services/link-generators-v2/nextleader-link-generator-v2.service';
import { AppNavSavvasLinkingService } from 'src/app/services/app-nav-v2/app-nav-savvas-linking.service';
import { ProductAppCodes } from 'src/app/services/product-info/product-info.service';
import { AppNavV2 } from '../models/app-nav-v2.model';

export interface SideNavItem {
  appNavItem: AppNavV2;
  stateIsOpen: boolean;
  disableLinkSelectorAccordions?: boolean;
}

@Component({
  selector: 'app-left-nav',
  templateUrl: './left-nav.component.html',
  styleUrls: ['./left-nav.component.scss'],
})
export class LeftNavComponent implements OnInit, OnDestroy {
  @ViewChildren(AppLinkSelectorComponent)
  viewChildren!: AppLinkSelectorComponent[];
  @Input() sendFeedbackId = 'send-feedback';

  public availableApps: AppNavV2[] = [];
  public availableData: AppNavV2[] = [];
  public window: Window;
  public homeUri: string = '';
  public optOutUri: string = '';
  public sideNavs: SideNavItem[] = [];
  public appNavDegraded: boolean = false;
  public hideOptOut: boolean = false;
  public isIntroAnimationComplete: boolean = false;
  public playFadeInAnimation: boolean = false;
  public isAdmin: boolean = false;
  public availableV2Apps: AppNavV2[] = [];
  public otherAvailableApps: AppNavV2[] = [];
  hasFreckleLicense: boolean = true;
  private subscriptions: Subscription[] = [];


  constructor(
    @Inject(DOCUMENT) private document: Document,
    public dialog: MatDialog,
    private _userService: UserService,
    public featureToggleService: FeatureToggleService,
    private _adminHandlerService: AdminHandlerService,
    private _introAnimationService: IntroAnimationService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _appNavV2Service: AppNavV2Service,
    private _launchControlService: LaunchControlService,
    private _nextLeaderLinkGeneratorV2Service: NextLeaderLinkGeneratorV2Service,
    private appNavSavvasLinking: AppNavSavvasLinkingService
  ) {
    this.window = this.document.defaultView as Window;
  }

  async ngOnInit() {
    let subscription = this._introAnimationService.leftNavAnimationStatus$.subscribe((animationStatus: IntroAnimationStatus | undefined) => {
      if (animationStatus) {
        this.checkAnimationStatus(animationStatus);
      }
    });
    this.subscriptions.push(subscription);
    //Forces a change detection on animation status to prevent NG100: ExpressionChangedAfterItHasBeenCheckedError
    this._changeDetectorRef.detectChanges();

    await this.setAppV2Data();

    this.otherAvailableApps = await this._appNavV2Service.getOtherAvailableApps();

    this.homeUri = this._userService.getHomeUri();

    this.optOutUri = `${this.homeUri}api/beta/optout`;
    //change optOutUri if renaissance next is released
    let betaIsOver = this._userService.getBetaIsOver();
    if (betaIsOver) {
      this.optOutUri = `${this.homeUri}api/next/optout`;
    }
    this.hideOptOut = this._userService.getHideOptOut();
    this.isAdmin = this._adminHandlerService.isAdmin();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public pendoTrackFeedback() {
    const window = this.document.defaultView as any;
    if (window.pendo) {
      const pendo = window.pendo;
      pendo.track('Feedback');
    }
  }

  public showOptOutModal(event: MouseEvent) {
    event.preventDefault();

    this.dialog.open(OptOutDialogComponent, {
      data: {
        optOutUri: this.optOutUri,
      },
    });
  }

  public directAppLinkClick(app: AppNavV2, type: string) {
    if (type === 'v2') {
      this.navigateV2(app);
    } else if (type === 'other' && app.appCode === ProductAppCodes.Savvas) {
      this.appNavSavvasLinking.navigateToSavvas(app);
    }
  }

  public isRenU(app: AppNavV2): boolean {
    return app.appCode === ProductAppCodes.RENU;
  }

  public closeSidenav() {
    // This moves focus to the default on page load so the sidenav icon doesn't stay in color
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    let currentSideNavAppCode;
    // Close all side navs
    for (let sideNav of this.sideNavs) {
      if (sideNav.stateIsOpen) {
        currentSideNavAppCode = sideNav.appNavItem.appCode;
      }
      sideNav.stateIsOpen = false;
    }

    // Close all open panels
    if (this.viewChildren && this.viewChildren.length > 0) {
      this.viewChildren.forEach((child) => {
        child.clearPanelState();
      });
    }

    // Move focus to the current button
    document.getElementById(`${currentSideNavAppCode}`)?.focus();
  }

  public hasSidenav(app: AppNavV2): boolean {
    if (this.sideNavs.find((x) => x.appNavItem === app)) {
      return true;
    }
    return false;
  }

  public isSidenavExpanded(app: AppNavV2): boolean {
    for (let sideNav of this.sideNavs) {
      if (sideNav.appNavItem.appCode === app.appCode) {
        return sideNav.stateIsOpen;
      }
    }
    return false;
  }

  public goToLeaderRedirectUrl(): void {
    let appCode = this._nextLeaderLinkGeneratorV2Service.getNextLeaderAppCode();
    this._launchControlService.launchApp(appCode);
  }

  private getSideNavBlades(apps: AppNavV2[], dataApps: AppNavV2[]) {
    let sideNavItems: SideNavItem[] = [];
    let allApps = [...apps, ...dataApps];
    for (let topLevelAppNav of allApps) {
      if (topLevelAppNav.children) {
        // REPORTS disable accordions
        let sideNavItem: SideNavItem = {
          appNavItem: topLevelAppNav,
          stateIsOpen: false,
          disableLinkSelectorAccordions: topLevelAppNav?.appCode === ProductAppCodes.Reports,
        };
        sideNavItems.push(sideNavItem);
      }
    }

    return sideNavItems;
  }

  public optOutRedirect() {
    this.window.location.href = this.optOutUri;
  }

  public checkAnimationStatus(animationStatus: IntroAnimationStatus) {
    if (animationStatus.complete) {
      this.isIntroAnimationComplete = true;
    }
    if (animationStatus.enabled) {
      this.playFadeInAnimation = true;
      //turn off animation after it has been played
      setTimeout(() => { this.playFadeInAnimation = false }, 400);
    }
  }

  // offsets each element by its order in the list and the height of the element
  // They will all start at the position of the first element before sliding down to their final spot
  public getAppNavTranslateOffset(appNavItem: AppNavV2) {
    let linkLogoContainer = this.document.getElementsByClassName('link-logo-container');
    let linkLogoStyle = getComputedStyle(linkLogoContainer[0]);
    let totalHeight = parseInt(linkLogoStyle.marginBottom) + parseInt(linkLogoStyle.height);
    let index = this.availableApps.findIndex(item => item == appNavItem);

    return `${index * -totalHeight}px`;
  }

  // App nav v2 functions
  public navigateV2(app: AppNavV2) {
    // Sidenav
    let maybeSideNav = this.sideNavs.find((x) => x.appNavItem === app);
    if (maybeSideNav) {
      maybeSideNav.stateIsOpen = true;
    }
    else if (app.url) {
      this.window.location = app.url;
    }
    else {
      if (app.appCode) {
        if(app.appCode === ProductAppCodes.Freckle && app.url && !this.hasFreckleLicense) {
          this._launchControlService.launchApp(app.appCode, app.url, false, true);

        } else if (app.appCode) {
          this._launchControlService.launchApp(app.appCode, app.deepLink, false, true);
        }
      }
    }
  }
  private hasFreckleApp(): boolean {
    return this.availableV2Apps.filter(app => app.appCode ===  ProductAppCodes.Freckle)?.length > 0;
  }

  private async setAppV2Data() {
    await this._appNavV2Service.initializeAppNavV2();
    this.appNavDegraded = this._appNavV2Service.getIsDegraded();
    if (this.appNavDegraded == false) {
      this.availableV2Apps = await this._appNavV2Service.getAvailableV2Apps();

      /* 
        IMPORTANT: This is the source of the incorrect left nav sorting 
        which moves Educator Academy up from the bottom (where it should be).
        Make sure to fix it before turning the Freckle experiment toggle back on.
      */  
      if (await this.featureToggleService.isTrueAsync('freckle-left-nav-experiment') && !this.hasFreckleApp()) {
        let freckleApp: AppNavV2 = {
          appCode: ProductAppCodes.FrecklePlg,
          displayName: "Freckle",
          imagePrefix: "freckle_logo",
          url: "https://freckle.com?referrer=ren_next"
        };

        this.hasFreckleLicense = false;
        this.availableV2Apps.unshift(freckleApp);
        this.availableV2Apps = this.availableV2Apps.sort((a, b) => a.displayName.localeCompare(b.displayName));
      }
      this.availableData = this._appNavV2Service.getAvailableV2MyData();
      this.generateLeftNav();
    }
    else {
      this.availableV2Apps = await this._appNavV2Service.getAppsForDegradedMode();
      this.generateLeftNav();
    }
  }
  private generateLeftNav() {
    this.sideNavs = this.getSideNavBlades(
      this.availableV2Apps,
      this.availableData
    );
  }
}
